import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import {
    Button, FormField, Text, Checkbox, LottieLoadingIndicator, Toast, ImageCropModal,
    SegmentedButtonGroup, SharedDateInput, StandardModal, StandardModalActionButton,
    StandardModalActionGroup, StandardModalDivider, portalColors
} from "src/components";
import { Box, Grid } from "@mui/material";
import styled from "styled-components";
import AttachIcon from "src/img/new/attach-image.svg";
import moment from "moment";
import Dropzone from "react-dropzone";
import RemoveImg from "src/img/close.png";
import { DndContext } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import SortableInputField from "./SortableInputField";
import { API_URL } from "src/scenes/App";
import pluralize from "pluralize";
import { capitalizeString, customNames } from "src/utils/helpers";
import { ASPECT_RATIO_LABEL } from "src/scenes/Noticeboard/constants";

const Label = styled.label`
    font-family: Manrope, Helvetica, sans-serif;
    font-size: ${p => p.size || "0.906rem"};
    font-weight: 700;
    color: ${portalColors.text};
    display: block;
    margin-bottom: ${p => p.mb || ".5rem"};
`;

const Note = styled.label`
    font-style: normal;
    font-weight: 400;
    font-size: ${p => p.size || "16px"};
    line-height: 19px;
    font-family: Manrope, Helvetica, sans-serif;
    color: ${portalColors.mutedText};
    display: inline-block;
    margin-bottom: 1.875rem;
    max-width: 707px;
`;

const StyledTitleInput = styled(FormField)`
    height: 3.125rem;
    border: 1px solid ${portalColors.borderStrong};
    border-radius: 8px;
    text-align: left;
    font-family: Manrope, Helvetica, sans-serif;
    font-weight: 400;
    font-size: 1rem;
    color: ${portalColors.text};
    box-shadow: none;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;

    ::placeholder {
        text-align: left;
        color: ${portalColors.textSoft};
        font-family: Manrope, Helvetica, sans-serif;
    }
`;

const TextArea = styled.textarea`
    font-family: Manrope, Helvetica, sans-serif;
    color: ${portalColors.text};
    font-size: 1rem;
    font-weight: 400;
    height: 15rem;
    width: 100%;
    box-sizing: border-box;
    border: 1px solid ${portalColors.borderStrong};
    border-radius: 8px;
    padding: 10px 20px 10px 20px;
    margin: 0.313rem 0;
    resize: none;
    box-shadow: none;

    &:focus {
        outline: none;
        border: 1px solid ${portalColors.text};
    }

    ::placeholder {
        text-align: left;
        color: ${portalColors.textSoft};
        font-family: Manrope, Helvetica, sans-serif;
        font-size: 0.875rem;
    }
`;

const Description = styled.div`
    font-weight: ${p => p.weight || "500"};
    font-size: ${p => p.size || "0.813rem"};
    color: ${p => p.color || "#000"}
`;

const StyledDropzone = styled(Dropzone)`
    width: min(100%, 18rem);
    aspect-ratio: 16 / 9;
    height: auto;
    border: 1px solid #8291b2;
    border-radius: 12px;
    margin: 0.313rem 0;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    overflow: hidden; /* Ensure image doesn't overflow the container */
    box-shadow: 0px 3px 6px #00000029;
`;

const ImagePreview = styled.img`
    width: 100%;
    height: 100%;
    object-fit: contain; /* Image will fit inside container without stretching or cropping */
    display: block;
`;

const PlaceholderImage = styled.img`
    width: 2.75rem;
    height: 2.75rem;
    object-fit: contain;
`;

const FileInput = styled.input`
    display: none;
`;

const CheckboxContainer = styled.div`
    display: flex;
    align-items: center;
`;

const DateContainer = styled.div`
    width: 8.75rem;
    text-align: center;
`;

const SelectedDate = styled.span`
    display: inline-block;
    font-size: 0.875rem;
    color: #171A22;
    margin-top: 0.5rem;
`;

const ErrorMessage = styled.span`
    font-size: 0.844rem;
    color: #ff7171;
`;

const FormDivider = styled(StandardModalDivider)`
    margin: 1rem 0;
`;

const RemoveButton = styled.img`
    height: 40px;
    width: 40px;
    cursor: pointer;
    margin-top: 30px;
    margin-left: 3px;
`;

const VISIBLE_TO = {
    EMPLOYEES: "employees",
    CLIENTS: "clients",
    EMPLOYEES_AND_CLIENTS: "employees_and_clients"
};

const NON_SORTABLE_FIELDS = [
    "title",
    "company",
    "position",
    "department"
];

const ContactModal = ({
    hideContactModal,
    handleInputChange,
    handleVisibleTo,
    handleDateCheckbox,
    handleDateChange,
    isNoExpiration,
    handleCustomFieldValue,
    formData,
    setFormData,
    formErrors,
    hasFormErrors,
    updatingUuid,
    handleCreateOrUpdate,
    showDeleteDialog,
    isFormLoading,
    handleRemoveCustomField,
    handleCreateCustomField,
    customDirectoryName,
    user
}) => {
    // Fields list
    const fieldDefinitions = [
        { name: "title", label: "Person's Name", placeholder: "Enter your name here..." },
        { name: "company", label: "Company Name", placeholder: "Enter your name here..." },
        { name: "position", label: "Title/Position", placeholder: "Enter your position here..." },
        { name: "department", label: "Department", placeholder: "Enter your department here..." },
        { name: "email", label: "Email Address", placeholder: "Enter your email here..." },
        { name: "phoneNumber", label: "Phone Number", placeholder: "Enter your phone number here..." },
        { name: "address", label: "Address", placeholder: "Enter your address here..." },
        { name: "country", label: "Country/Location", placeholder: "Enter your country or location here..." },
        { name: "website", label: "Website", placeholder: "Enter your website here..." },
    ];

    // Exclude non-sortable fields from fields list
    const sortableFieldDefinitions = fieldDefinitions.filter(
        field => !NON_SORTABLE_FIELDS.includes(field.name)
    );
    const defaultSortableOrder = sortableFieldDefinitions.map(f => f.name);

    // Map custom field name
    const getCustomFieldNameByUuid = (uuid) => {
        const index = formData.customFields.findIndex(field => field.uuid === uuid);
        if (index < 0) {
            return "";
        }
        const name = formData.customFields[index]?.name || "";
        return name.trim();
    };

    const [visibleTo, setVisibleTo] = useState("");
    const [selectedImage, setSelectedImage] = useState(null);
    const [isAddingField, setIsAddingField] = useState(false);
    const [newCustomFieldName, setNewCustomFieldName] = useState("");
    const [isSavingNewField, setIsSavingNewField] = useState(false);
    const [removingUuid, setRemovingUuid] = useState(null); // Track which field is being removed
    const dropzoneRef = useRef();
    const [isFieldOrderLoading, setIsFieldOrderLoading] = useState(true);
    const [fieldOrder, setFieldOrder] = useState(defaultSortableOrder);
    const [customFieldOrder, setCustomFieldOrder] = useState(
        formData.customFields.map(field => field.uuid)
    );

    // Crop Image
    const [imageToCrop, setImageToCrop] = useState(null);
    const [croppedBlob, setCroppedBlob] = useState(null);
        
    const handleCropComplete = (file) => {
        if (!file) {
            return;
        }
        const url = URL.createObjectURL(file);
        if (selectedImage) {
            URL.revokeObjectURL(selectedImage);
        }

        setSelectedImage(url); // For preview
        setImageToCrop(null);
        setCroppedBlob(file);
    };
    
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = fieldOrder.indexOf(active.id);
        const newIndex = fieldOrder.indexOf(over.id);
        if (oldIndex < 0 || newIndex < 0) return;

        const nextCoreOrder = arrayMove(fieldOrder, oldIndex, newIndex);
        setFieldOrder(nextCoreOrder);

        saveFieldOrder(nextCoreOrder, customFieldOrder);
    };

    const handleCustomFieldDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = customFieldOrder.indexOf(active.id);
        const newIndex = customFieldOrder.indexOf(over.id);
        if (oldIndex < 0 || newIndex < 0) return;

        const nextCustomOrder = arrayMove(customFieldOrder, oldIndex, newIndex);
        setCustomFieldOrder(nextCustomOrder);

        // Reorder array in formData so that field and their values are correct 
        setFormData(prev => ({
            ...prev,
            customFields: nextCustomOrder.map(uuid => prev.customFields.find(field => field.uuid === uuid)).filter(Boolean)
        }));

        saveFieldOrder(fieldOrder, nextCustomOrder);
    };

    // Get Field Order and apply
    useEffect(() => {
        if (updatingUuid) {
            getFieldOrder();
        } else {
            const filteredOrder = defaultSortableOrder.filter(
                name => !NON_SORTABLE_FIELDS.includes(name)
            );
            setFieldOrder(filteredOrder);
            setIsFieldOrderLoading(false);
        }
    }, [updatingUuid]);

    // Fetch saved field order
    const getFieldOrder = async () => {
        if (!updatingUuid) return;

        setIsFieldOrderLoading(true);

        try {
            const res = await axios.get(`${API_URL}/businessContacts/${updatingUuid}/fieldOrder`, {
                headers: {
                    Authorization: "Bearer " + user.token,
                    "Content-Type": "application/json"
                }
            });

            const savedOrder = res.data?.contact?.fieldOrder ?? [];
            const coreSet = new Set(
                fieldDefinitions
                    .map(d => d.name)
                    .filter(n => !NON_SORTABLE_FIELDS.includes(n))
            );
            const coreOnly = (Array.isArray(savedOrder) ? savedOrder : []).filter(n => coreSet.has(n)); 

            setFieldOrder(coreOnly.length > 0 ? coreOnly : defaultSortableOrder);

        } catch (error) {
            console.error("Failed to fetch field order", error);
            Toast.error("Unable to load saved field order");
            setFieldOrder(defaultSortableOrder);
        } finally {
            setIsFieldOrderLoading(false);
        }
    };

    // Save field order
    const saveFieldOrder = async (coreOrder, customOrderIds = customFieldOrder) => {
        if (!updatingUuid) return;

        const core = Array.isArray(coreOrder) ? coreOrder : [];
        const customIds = Array.isArray(customOrderIds) ? customOrderIds : [];

        // Convert custom UUIDs -> names
        const customNames = customIds
            .map(getCustomFieldNameByUuid)
            .map(s => s.trim())
            .filter(s => s.length > 0);

        const payload = [...core, ...customNames];

        try {
            await axios.put(`${API_URL}/businessContacts/${updatingUuid}/fieldOrder`, { fieldOrder: payload }, {
                headers: {
                    Authorization: "Bearer " + user.token,
                    "Content-Type": "application/json"
                }
            });
        } catch (error) {
            console.error("Failed to update field order", error);
            Toast.error("Failed to update field order.");
        }
    };

    const handleRemoveCustomFieldLocal = async (field) => {
        try {

            const maybePromise = handleRemoveCustomField ? handleRemoveCustomField(field, setRemovingUuid) : null;
            await Promise.resolve(maybePromise);

            await new Promise(r => setTimeout(r, 0));

            const validUuids = formData.customFields.map(field => field.uuid);

            // Prune local order and persist
            setCustomFieldOrder((prev) => {
                const next = prev.filter(uuid => validUuids.includes(uuid));

                // save defaults (current) + new custom order
                saveFieldOrder(fieldOrder, next);
                return next;
            });
        } catch (err) {
            console.error("Delete custom field failed:", err);
            Toast.error(err.getApiErrorDetails().message);
        } finally {
            setRemovingUuid(null);
        }
    };

    useEffect(() => {
        const latestUuids = formData.customFields.map(field => field.uuid);

        const isSame =
            latestUuids.length === customFieldOrder.length &&
            latestUuids.every((uuid, i) => uuid === customFieldOrder[i]);

        if (!isSame) {
            const idSet = new Set(latestUuids);
            const preserved = customFieldOrder.filter(uuid => idSet.has(uuid));
            const added = latestUuids.filter(uuid => !preserved.includes(uuid));
            const next = [...preserved, ...added];

            setCustomFieldOrder(next);
            saveFieldOrder(fieldOrder, next);
        }
    }, [formData.customFields]);

    useEffect(() => {
        if (formData.visibleToEmployees && formData.visibleToClients) {
            setVisibleTo(VISIBLE_TO.EMPLOYEES_AND_CLIENTS);
        } else if (formData.visibleToEmployees) {
            setVisibleTo(VISIBLE_TO.EMPLOYEES);
        } else if (formData.visibleToClients) {
            setVisibleTo(VISIBLE_TO.CLIENTS);
        }
        // Set selectedImage if there's an image URL in formData
        if (formData.imageUrl) {
            setSelectedImage(formData.imageUrl);
        }
    }, [formData]);

    const handleNewCustomFieldChange = e => {
        setNewCustomFieldName(e.target.value);
    };

    const handleFormSubmit = () => {
        if (hasFormErrors()) {
            return;
        }
        // create/update contact
        if (croppedBlob && formData.imageUrl !== selectedImage) {
            handleCreateOrUpdate(croppedBlob);
        } else {
            handleCreateOrUpdate(null);
        }
    };

    const formatSelectedDate = date => {
        return moment(date).format("Do MMMM, YYYY");
    };

    const getErrorMessage = input => {
        const error = formErrors.find(error => error.input === input);
        return error ? error.message : null;
    };

    const handleDrop = acceptedFiles => {
        if (acceptedFiles && acceptedFiles.length > 0) {
            const file = acceptedFiles[0];

            const validImageTypes = ["image/jpeg", "image/png"];
            if (!validImageTypes.includes(file.type)) {
                Toast.error("Unsupported file type. Images may be JPEG or PNG format.");
                return;
            }
            
            const reader = new FileReader();
            reader.onload = () => {
                const img = new Image();
                img.onload = () => {
                    setImageToCrop(reader.result);
                };
                img.src = reader.result;
            };
            reader.readAsDataURL(file);
        }
    };

    const handleBrowseClick = () => {
        if (dropzoneRef.current) {
            dropzoneRef.current.open();
        }
    };

    const handleAddFieldClick = () => {
        setIsAddingField(true);
    };

    const handleCancelClick = () => {
        setIsAddingField(false);
    };

    return (
        <StandardModal
            open={true}
            title={updatingUuid ? `Edit ${customDirectoryName} Listing` : `New ${customDirectoryName} Listing`}
            widthMode="medium"
            bodyPadding="0"
            hideCloseButton
            footer={(
                <div style={{ display: "flex", justifyContent: "space-between", width: "100%", gap: "0.75rem", flexWrap: "wrap" }}>
                    <div>
                        {updatingUuid && !isFormLoading && (
                            <StandardModalActionButton
                                type="button"
                                $variant="danger"
                                onClick={showDeleteDialog}
                            >
                                Delete
                            </StandardModalActionButton>
                        )}
                    </div>
                    <StandardModalActionGroup $pushRight>
                        {isFormLoading ? <LottieLoadingIndicator height="1.563rem" width="1.563rem" containerHeight="0" /> : (
                            <>
                                <StandardModalActionButton
                                    type="button"
                                    onClick={hideContactModal}
                                >
                                    Cancel
                                </StandardModalActionButton>
                                <StandardModalActionButton
                                    type="button"
                                    $variant="primary"
                                    onClick={() => handleFormSubmit()}
                                >
                                    Save
                                </StandardModalActionButton>
                            </>
                        )}
                    </StandardModalActionGroup>
                </div>
            )}
        >
            <Box sx={{ padding: "1.25rem", width: "100%", boxSizing: "border-box" }}>
                <Label size="1.125rem" style={{ marginBottom: "0.625rem" }}>Visible to</Label>
                <SegmentedButtonGroup
                    options={[
                        { value: VISIBLE_TO.EMPLOYEES, label: capitalizeString(pluralize(customNames.employees)) },
                        { value: VISIBLE_TO.CLIENTS, label: capitalizeString(pluralize(customNames.clients)) },
                        { value: VISIBLE_TO.EMPLOYEES_AND_CLIENTS, label: `${capitalizeString(pluralize(customNames.employees))} & ${capitalizeString(pluralize(customNames.clients))}` }
                    ]}
                    value={visibleTo}
                    minWidth="10rem"
                    onChange={handleVisibleTo}
                />
                {getErrorMessage("visibleTo") && <ErrorMessage>{getErrorMessage("visibleTo")}</ErrorMessage>}

                {/* Right Column: Image Container */}
                <Grid item xs={12} md={5} style={{ marginTop: "1.5rem", marginBottom: "30px" }}>
                    <Label size="1.125rem">{updatingUuid ? "Change" : "Upload"} Image (Optional)</Label>
                    <StyledDropzone ref={dropzoneRef} onDrop={handleDrop}>
                        {selectedImage ? (
                            <ImagePreview src={selectedImage} alt="Uploaded" />
                        ) : (
                            <PlaceholderImage src={AttachIcon} alt="Attach icon" />
                        )}
                    </StyledDropzone>
                    <FileInput type="file" accept="image/*" onChange={handleDrop} />
                    <Button
                        backgroundColor="#000"
                        color="#fff"
                        width="8.75rem"
                        height="2.375rem"
                        style={{ borderRadius: "7px", marginTop: "10px", boxShadow: "none" }}
                        onClick={handleBrowseClick}
                    >
                        Browse File
                    </Button>
                </Grid>

                <Grid container spacing={3}>
                    {/* Left Column: Inputs */}
                    <Grid item xs={12} >
                        <Label size="1.25rem" style={{ marginBottom: "0.625rem" }}>Contact Information</Label>
                        <Note><strong>Note: </strong>You must enter at least one of the following to create a listing — either a Person’s Name or a Company Name. This helps users easily identify and search listings in the Directory.</Note>
                        <Box sx={{ border: "1px solid var(--calendar-border, #D8DEEC)", borderRadius: "12px", padding: "1rem", boxShadow: "none", background: "var(--calendar-surface, #FFFFFF)" }}>
                            <Grid container direction="column" spacing={2}>
                                {fieldDefinitions
                                    .filter(field => NON_SORTABLE_FIELDS.includes(field.name))
                                    .map(field => (
                                        <React.Fragment key={field.name}>
                                            <Grid item xs={12}>
                                                <Label>{field.label}</Label>
                                                <StyledTitleInput
                                                    name={field.name}
                                                    value={formData[field.name] ?? ""}
                                                    placeholder={field.placeholder}
                                                    onChange={handleInputChange}
                                                    border={getErrorMessage(field.name) ? `1px solid ${portalColors.danger}` : undefined}
                                                />
                                                {getErrorMessage(field.name) && (
                                                    <ErrorMessage>{getErrorMessage(field.name)}</ErrorMessage>
                                                )}
                                            </Grid>
                                            <FormDivider />
                                        </React.Fragment>
                                    ))
                                }
                                <DndContext onDragEnd={handleDragEnd}>
                                    {isFieldOrderLoading ? (
                                        <div style={{ textAlign: "center", marginTop: "2rem" }}>
                                            <LottieLoadingIndicator height="2rem" width="2rem" containerHeight="0" margin="1rem 0 0 5px" />
                                        </div>
                                    ) : (
                                        <SortableContext items={fieldOrder} strategy={verticalListSortingStrategy}>
                                            {fieldOrder.map(fieldName => {
                                         
                                                const field = fieldDefinitions.find(f => f.name === fieldName);
                                                return (
                                                    <React.Fragment key={field.name}>
                                                        <Grid item xs={12}>
                                                            <SortableInputField id={field.name}>
                                                                <>
                                                                    <Label>{field.label}</Label>
                                                                    <StyledTitleInput
                                                                        name={field.name}
                                                                        value={formData[field.name] ?? ""}
                                                                        placeholder={field.placeholder}
                                                                        onChange={handleInputChange}
                                                                        border={getErrorMessage(field.name) ? `1px solid ${portalColors.danger}` : undefined}
                                                                    />
                                                                    {getErrorMessage(field.name) && (
                                                                        <ErrorMessage>{getErrorMessage(field.name)}</ErrorMessage>
                                                                    )}
                                                                </>
                                                            </SortableInputField>
                                                        
                                                        </Grid>
                                                        <FormDivider />
                                                    </React.Fragment>
                                                );
                                            })}
                                        </SortableContext>
                                    )}
                                </DndContext>
                                {/* Show Custom Fields (own sortable context) */}
                                {formData.customFields.length > 0 && (
                                    <>
                                        <DndContext onDragEnd={handleCustomFieldDragEnd}>
                                            <SortableContext
                                                items={customFieldOrder}
                                                strategy={verticalListSortingStrategy}
                                            >
                                                {customFieldOrder.map((uuid) => {
                                                    const index = formData.customFields.findIndex(field => field.uuid === uuid);
                                                    const field = formData.customFields[index];
                                                    if (!field) {
                                                        return null;
                                                    }

                                                    return (
                                                        <Grid item key={uuid}>
                                                            <SortableInputField id={uuid}>
                                                                <>
                                                                    <Grid container direction="row" alignItems="center">
                                                                        <Grid item xs>
                                                                            <Label>{field.name}</Label>
                                                                            <StyledTitleInput
                                                                                value={field.value ?? ""}
                                                                                placeholder={`Enter your ${field.name} here...`}
                                                                                onChange={e => handleCustomFieldValue(e, field.name)}
                                                                            />
                                                                        </Grid>
                                                                        {!isFormLoading && (
                                                                            <Grid item md={1}>
                                                                                {removingUuid !== uuid ? (
                                                                                    <RemoveButton
                                                                                        src={RemoveImg}
                                                                                        alt="close image"
                                                                                        onClick={() => handleRemoveCustomFieldLocal(field)}
                                                                                    />
                                                                                ) : (
                                                                                    <LottieLoadingIndicator
                                                                                        height="2rem"
                                                                                        width="2rem"
                                                                                        containerHeight="0"
                                                                                        margin="1rem 0 0 5px"
                                                                                    />
                                                                                )}
                                                                            </Grid>
                                                                        )}
                                                                    </Grid>
                                                                </>
                                                            </SortableInputField>
                                                            {index < customFieldOrder.length - 1 && <FormDivider />}
                                                        </Grid>
                                                    );
                                                })}
                                            </SortableContext>
                                        </DndContext>
                                    </>
                                )}
                                <FormDivider />
                                <Grid item xs={12}>
                                    <Label>Create Custom Field</Label>
                                    {!isAddingField && (
                                        <>
                                            <Description size="0.75rem" weight="400" color="#8291b2">
                                                Create custom fields and display specific details of employees, companies <br />
                                                or members of your business to your app users.
                                            </Description>
                                            <Button
                                                backgroundColor="#000"
                                                color="#fff"
                                                width="8rem"
                                                height="2.375rem"
                                                style={{ borderRadius: "7px", marginTop: "10px" }}
                                                onClick={handleAddFieldClick}
                                            >
                                                Add Field
                                            </Button>
                                        </>
                                    )}
                                    {isAddingField && (
                                        <>
                                            <StyledTitleInput
                                                placeholder="Enter new field name"
                                                onChange={e => handleNewCustomFieldChange(e)}
                                            />
                                            <Grid
                                                container
                                                direction="row"
                                                justifyContent="flex-end"
                                                alignItems="center"
                                                style={{ marginTop: "7px" }}
                                            >
                                                {isSavingNewField ? (
                                                    <div style={{ textAlign: "right", marginRight: "0.625rem" }}>
                                                        <LottieLoadingIndicator height="2rem" width="2rem" containerHeight="0" margin="0" />
                                                    </div>
                                                ) : (
                                                    <>
                                                        <Button
                                                            backgroundColor="#fff"
                                                            color="#000"
                                                            width="6.063rem"
                                                            height="2.375rem"
                                                            border="1px solid #000"
                                                            style={{ borderRadius: "7px", display: "flex", alignItems: "center", justifyContent: "center" }}
                                                            onClick={handleCancelClick}
                                                        >
                                                            Cancel
                                                        </Button>

                                                        <Button
                                                            backgroundColor="#000"
                                                            color="#fff"
                                                            width="6.063rem"
                                                            height="2.375rem"
                                                            style={{ borderRadius: "7px", marginLeft: "10px" }}
                                                            onClick={() => handleCreateCustomField(
                                                                newCustomFieldName,
                                                                setIsAddingField,
                                                                setNewCustomFieldName,
                                                                setIsSavingNewField
                                                            )}
                                                        >
                                                            Save
                                                        </Button>
                                                    </>
                                                )}
                                            </Grid>
                                        </>
                                    )}
                                </Grid>
                                <FormDivider />
                                <Grid item xs={12}>
                                    <Label>Description</Label>
                                    <TextArea
                                        name="description"
                                        value={formData.description ?? ""}
                                        placeholder="Enter your description here..."
                                        onChange={handleInputChange}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container direction="column" spacing={2} style={{ marginBottom: "1.2rem" }}>
                                <Grid item xs={12}>
                                    <Label size="1.25rem" mb="1rem" style={{ marginTop: "1.5rem" }}>Contact Expiry</Label>
                                    <Description size="14px" weight="400">
                                        Set expiry dates for contacts that have a limited timeframe in your Directory, and
                                        we’ll remind you when it’s time to remove them. For ongoing contacts, simply select
                                        ‘None’ and the contact will remain seamlessly in your Me Business directory.
                                    </Description>
                                </Grid>
                                <Grid item xs={12}>
                                    <Label>Select 1 Option:</Label>
                                    <Grid container spacing={2} alignItems="center" style={{ marginTop: "3px" }}>
                                        <Grid item xs={6}>
                                            <SharedDateInput
                                                value={formData.expirationDate}
                                                onChange={handleDateChange}
                                                placeholder={formData.expirationDate ? "Change expiry" : "Set expiry date"}
                                                popperPlacement="top-start"
                                            />
                                            {formData.expirationDate && (
                                                <DateContainer>
                                                    <SelectedDate>
                                                        {formatSelectedDate(formData.expirationDate)}
                                                    </SelectedDate>
                                                </DateContainer>
                                            )}
                                        </Grid>
                                        <Grid item xs={6}>
                                            <CheckboxContainer>
                                                <Label>
                                                    <Checkbox
                                                        color="#36be24"
                                                        height="38px"
                                                        width="38px"
                                                        padding="7px"
                                                        radius="12px"
                                                        checked={isNoExpiration}
                                                        onChange={handleDateCheckbox}
                                                    />
                                                </Label>
                                                <Text
                                                    color="#000"
                                                    size="0.875rem"
                                                    weight="500"
                                                    margin="0 0 0 10px"
                                                >
                                                    None
                                                </Text>
                                            </CheckboxContainer>
                                        </Grid>
                                    </Grid>
                                    {getErrorMessage("expirationDate") && <ErrorMessage style={{ marginTop: "0.625rem" }}>{getErrorMessage("expirationDate")}</ErrorMessage>}
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
            <ImageCropModal
                imageSrc={imageToCrop}
                isOpen={imageToCrop !== null}
                onClose={() => setImageToCrop(null)}
                onCropComplete={handleCropComplete}
                aspectRatios={[
                    { width: 16, height: 9, label: "16:9", standardName: ASPECT_RATIO_LABEL.WIDESCREEN },
                ]}
                allowedOrientations={["landscape"]}
                lockedAspectRatio={undefined}
            />
        </StandardModal>
    );
};

export default ContactModal;
